import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { 
  Zap, 
  ChevronDown, 
  ChevronRight, 
  Plus, 
  Calendar, 
  Box,
  ArrowRight
} from 'lucide-react';
import { getProjects, getFlows, createFlow } from '../../../services/api';
import { formatDistanceToNow } from 'date-fns';

const ComposerFlowsBlock = ({ user, size = 'medium', refreshTrigger }) => {
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [flows, setFlows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [flowsLoading, setFlowsLoading] = useState(false);
  const [showProjectDropdown, setShowProjectDropdown] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [creating, setCreating] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });
  const dropdownRef = useRef(null);

  useEffect(() => {
    loadProjects();
  }, [user, refreshTrigger]);

  useEffect(() => {
    if (selectedProject) {
      loadFlows();
    }
  }, [selectedProject]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowProjectDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const loadProjects = async () => {
    if (!user) return;
    
    try {
      const projectsData = await getProjects(user.id);
      setProjects(projectsData);
      
      // Auto-select first project if available
      if (projectsData.length > 0 && !selectedProject) {
        setSelectedProject(projectsData[0]);
      }
    } catch (error) {
      console.error('Failed to load projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadFlows = async () => {
    if (!selectedProject) return;
    
    try {
      setFlowsLoading(true);
      const flowsData = await getFlows(selectedProject.id);
      setFlows(flowsData);
    } catch (error) {
      console.error('Failed to load flows:', error);
      setFlows([]);
    } finally {
      setFlowsLoading(false);
    }
  };

  const handleCreateFlow = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !selectedProject) return;

    try {
      setCreating(true);
      const newFlow = await createFlow({
        name: formData.name,
        description: formData.description,
        projectId: selectedProject.id,
        nodes: [],
        connections: []
      });
      
      setFormData({ name: '', description: '' });
      setShowCreateForm(false);
      loadFlows();
      
      // Navigate to the new flow
      window.location.href = `/projects/${selectedProject.id}/composer/${newFlow.id}`;
    } catch (error) {
      console.error('Error creating flow:', error);
    } finally {
      setCreating(false);
    }
  };

  const getDisplayCount = () => {
    switch (size) {
      case 'small':
        return 2;
      case 'medium':
        return 3;
      case 'large':
        return 5;
      default:
        return 3;
    }
  };

  const displayFlows = flows.slice(0, getDisplayCount());

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (projects.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center">
        <Zap className="h-8 w-8 text-gray-400 dark:text-gray-500 mb-2" />
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">No projects yet</p>
        <Link to="/dashboard" className="text-xs text-celadon-600 hover:text-celadon-700 dark:text-celadon-400 dark:hover:text-celadon-300">
          Create a project first
        </Link>
      </div>
    );
  }

  return (
    <div className="h-full overflow-hidden">
      {/* Header with project selector */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Zap className="h-4 w-4 text-gray-600 dark:text-gray-400" />
          <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">Composer Flows</span>
        </div>
        {flows.length > getDisplayCount() && (
          <Link 
            to={`/projects/${selectedProject?.id}/composer`}
            className="text-xs text-celadon-600 hover:text-celadon-700 dark:text-celadon-400 dark:hover:text-celadon-300 font-medium transition-colors"
          >
            View all
          </Link>
        )}
      </div>

      {/* Project Selector */}
      <div className="relative mb-4" ref={dropdownRef}>
        <button
          onClick={() => setShowProjectDropdown(!showProjectDropdown)}
          className="w-full flex items-center justify-between px-3 py-2 border-b border-gray-200 dark:border-gray-600 bg-gray-50/80 hover:bg-gray-100/80 dark:bg-gray-700/50 dark:hover:bg-gray-600/50 rounded-2xl transition-all duration-200 text-left border border-gray-100/50 hover:border-gray-200/80 dark:border-gray-600/50 dark:hover:border-gray-500/80 hover:shadow-[0_2px_12px_-4px_rgba(0,0,0,0.08)] dark:hover:shadow-[0_2px_12px_-4px_rgba(0,0,0,0.3)]"
        >
          <div className="flex-1 min-w-0">
            <span className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate block">
              {selectedProject?.name || 'Select Project'}
            </span>
            {selectedProject && (
              <span className="text-xs text-gray-500 dark:text-gray-400 mt-1 block">
                {flows.length} flow{flows.length !== 1 ? 's' : ''}
              </span>
            )}
          </div>
          <ChevronDown className={`h-4 w-4 text-gray-400 dark:text-gray-500 transition-transform duration-200 ${showProjectDropdown ? 'rotate-180' : ''}`} />
        </button>

        {showProjectDropdown && (
          <div className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-gray-800 border border-gray-200/80 dark:border-gray-600/80 rounded-2xl shadow-[0_8px_40px_-8px_rgba(0,0,0,0.12)] dark:shadow-[0_8px_40px_-8px_rgba(0,0,0,0.3)] z-10 max-h-40 overflow-y-auto custom-scrollbar backdrop-blur-sm">
            {projects.map((project) => (
              <button
                key={project.id}
                onClick={() => {
                  setSelectedProject(project);
                  setShowProjectDropdown(false);
                }}
                className={`w-full text-left p-3 hover:bg-gray-50/80 dark:hover:bg-gray-700/50 transition-colors first:rounded-t-2xl last:rounded-b-2xl ${
                  selectedProject?.id === project.id ? 'bg-celadon-50 text-celadon-700 dark:bg-celadon-900/30 dark:text-celadon-300' : 'text-gray-900 dark:text-gray-100'
                }`}
              >
                <div className="text-sm font-medium truncate">{project.name}</div>
                {project.description && (
                  <div className="text-xs text-gray-500 dark:text-gray-400 truncate mt-1">{project.description}</div>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Flows Content */}
      <div className="flex-1 overflow-y-auto custom-scrollbar">
        {flowsLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-celadon-400"></div>
          </div>
        ) : showCreateForm ? (
          <div className="bg-gray-50/80 dark:bg-gray-700/50 rounded-2xl p-4 border border-gray-200/80 dark:border-gray-600/80">
            <form onSubmit={handleCreateFlow} className="space-y-3">
              <div>
                <label htmlFor="flow-name" className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Flow Name
                </label>
                <input
                  type="text"
                  id="flow-name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full text-sm border border-gray-300 dark:border-gray-600 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-celadon-300 focus:border-celadon-400 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                  placeholder="e.g., Content Generation Pipeline"
                  required
                  autoFocus
                />
              </div>
              
              {size !== 'small' && (
                <div>
                  <label htmlFor="flow-description" className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Description (optional)
                  </label>
                  <textarea
                    id="flow-description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full text-sm border border-gray-300 dark:border-gray-600 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-celadon-300 focus:border-celadon-400 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                    placeholder="Brief description of what this flow does"
                    rows={2}
                  />
                </div>
              )}
              
              <div className="flex space-x-2 pt-2">
                <button
                  type="submit"
                  disabled={creating || !formData.name.trim()}
                  className="flex-1 bg-celadon-600 text-white text-xs font-medium py-2 px-3 rounded-xl hover:bg-celadon-700 dark:bg-celadon-500 dark:hover:bg-celadon-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {creating ? 'Creating...' : 'Create Flow'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowCreateForm(false)}
                  className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        ) : flows.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <div className="bg-gray-100 dark:bg-gray-700 rounded-full p-3 mb-3">
              <Zap className="h-6 w-6 text-gray-400 dark:text-gray-500" />
            </div>
            <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">No flows yet</h4>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-4 max-w-xs">
              Create your first visual prompt engineering flow for this project
            </p>
            <button
              onClick={() => setShowCreateForm(true)}
              className="inline-flex items-center px-3 py-2 text-xs font-medium text-white bg-celadon-600 rounded-xl hover:bg-celadon-700 dark:bg-celadon-500 dark:hover:bg-celadon-600 transition-colors"
            >
              <Plus className="h-3 w-3 mr-1" />
              Create Flow
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {displayFlows.map((flow) => (
              <Link
                key={flow.id}
                to={`/projects/${selectedProject.id}/composer/${flow.id}`}
                className="block group"
              >
                <div className="bg-gray-50/80 hover:bg-gray-100/80 dark:bg-gray-700/50 dark:hover:bg-gray-600/50 rounded-2xl p-3 transition-all duration-200 border border-gray-100/50 hover:border-gray-200/80 dark:border-gray-600/50 dark:hover:border-gray-500/80 hover:shadow-[0_2px_12px_-4px_rgba(0,0,0,0.08)] dark:hover:shadow-[0_2px_12px_-4px_rgba(0,0,0,0.3)]">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 group-hover:text-celadon-700 dark:group-hover:text-celadon-300 transition-colors truncate">
                        {flow.name}
                      </h4>
                      {flow.description && size !== 'small' && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">
                          {flow.description}
                        </p>
                      )}
                      
                      <div className="flex items-center space-x-3 mt-2 text-xs text-gray-500 dark:text-gray-400">
                        <div className="flex items-center bg-gray-100/60 dark:bg-gray-600/60 px-2 py-1 rounded-lg">
                          <Box className="h-3 w-3 mr-1" />
                          {flow.node_count || 0} nodes
                        </div>
                        {size !== 'small' && (
                          <div className="flex items-center">
                            <Calendar className="h-3 w-3 mr-1" />
                            {formatDistanceToNow(new Date(flow.updated_at), { addSuffix: true })}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2 ml-3">
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                        <ArrowRight className="h-4 w-4 text-celadon-600 dark:text-celadon-400" />
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
            
            {flows.length > getDisplayCount() && (
              <Link
                to={`/projects/${selectedProject.id}/composer`}
                className="block text-center py-2 text-xs text-celadon-600 hover:text-celadon-700 dark:text-celadon-400 dark:hover:text-celadon-300 font-medium"
              >
                View all {flows.length} flows →
              </Link>
            )}

            {/* Add flow button at the bottom if not in create mode */}
            <button
              onClick={() => setShowCreateForm(true)}
              className="w-full flex items-center justify-center px-3 py-2 text-xs font-medium text-celadon-600 dark:text-celadon-400 bg-celadon-50 dark:bg-celadon-900/30 rounded-xl hover:bg-celadon-100 dark:hover:bg-celadon-900/50 transition-colors border border-celadon-200/50 dark:border-celadon-700/50"
            >
              <Plus className="h-3 w-3 mr-1" />
              New Flow
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ComposerFlowsBlock; 